import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/dashboard";
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] w-full flex flex-col items-center justify-center p-6 text-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Algo salió mal
          </h2>
          <p className="text-slate-600 max-w-md mb-6">
            Lo sentimos, ha ocurrido un error inesperado en esta sección.
            Nuestro equipo ha sido notificado.
          </p>
          {process.env.NODE_ENV === "development" && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 text-xs font-mono rounded-lg max-w-2xl overflow-auto text-left">
              {this.state.error?.message}
            </div>
          )}
          <Button
            onClick={this.handleReset}
            className="gap-2 bg-slate-900 hover:bg-slate-800"
          >
            <RefreshCw className="h-4 w-4" />
            Recargar aplicación
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
